import {
  LevelModifiers, NO_MODIFIERS, PlayerProfile, SupplyData, SupplyId, SuppliesData, SupplyStockJson, TrialModifierData, buySupply, supplyGold, farmBonusPercent, farmTwistFor,
  farmTwistModifiers, farmTwistsOpen, isPlainModifiers, offersAllowed, purchasesAllowed, utcDay, withoutSupplies, OfferScreen,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import type { LevelCatalog } from "./levelCatalog";
import { NOT_SAVING_HERE } from "./purchases";

/**
 * Pre-battle supplies (MP-06) and farm twists (MP-25) as use cases: the stock (domain SupplyStock on the profile, saved
 * in the `consumables` section like Research's), buying one for gems (hub only: never in a level or on the end screen),
 * arming them on the battle card, the day's twist for a cleared level and its switch, and `prepareRun`, which turns a
 * fresh battle's start into its run modifiers: the twist (a plain replay of a cleared level, switch on) and the armed
 * supplies, taken from the stock there and then. A battle that never starts (backed out of the load) takes nothing; one
 * that starts keeps what it took, whatever happens next (a quit, a crash, a loss).
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const consumablesSection = registerSaveSection<SupplyStockJson>("consumables", asObject, (v) => v);

export function suppliesFromSave(save: HasSections, profile: PlayerProfile): void { profile.supplies.load(consumablesSection.read(save)); }
export function suppliesToSave(save: HasSections, profile: PlayerProfile): void { consumablesSection.write(save, profile.supplies.toJson()); }

/** One battle-card well / Shop tile: the item, how many are held and whether it is armed for the next battle. */
export interface SupplySlot { readonly item: SupplyData; readonly have: number; readonly armed: boolean }

/** The day's twist for a level as the battle card shows it. */
export interface FarmTwistView { readonly modifier: TrialModifierData; readonly bonusPercent: number; readonly on: boolean }

export class Supplies {
  constructor(private readonly catalog: LevelCatalog) {}

  get data(): SuppliesData { return this.catalog.balance.commerce.shop.supplies; }
  private get stock() { return this.catalog.profile.supplies; }

  /** The Shop's Supplies section opens after `unlockAfterLevel`; the battle card shows wells once one is held or it opens. */
  get shopOpen(): boolean { return this.data.items.length > 0 && this.catalog.highestLevelCleared >= this.data.unlockAfterLevel; }
  get anyHeld(): boolean { return this.stock.total > 0; }
  get cardShows(): boolean { return this.data.items.length > 0 && (this.anyHeld || this.shopOpen); }

  slots(): SupplySlot[] { return this.data.items.map((item) => ({ item, have: this.stock.count(item.id), armed: this.stock.isArmed(item.id) })); }
  /** The battle card's tap: arm or disarm (one of each a battle). */
  toggle(id: SupplyId): ReturnType<PlayerProfile["supplies"]["toggle"]> { return this.stock.toggle(id); }

  /** One supply's gold price now (it follows the player's highest cleared level, like the gold packs). */
  goldPrice(item: SupplyData): number { return supplyGold(this.catalog.balance, item, this.catalog.highestLevelCleared); }

  /**
   * Buys one for gold (owner 2026-10-23): only from the hub (`screen`), never while the save cannot be written. Spends
   * once and adds one; the caller persists after a success.
   */
  buy(id: SupplyId, _now: number, screen: OfferScreen = "hub"): true | string {
    if (!purchasesAllowed(screen)) return "not here";
    if (!this.catalog.canPersist()) return NOT_SAVING_HERE;
    return buySupply(this.stock, this.catalog.profile, this.data, id, this.catalog.highestLevelCleared);
  }

  /** Whether the battle card may link to the Shop now: the hub, and not in the quiet after a loss (the never list). */
  shopLinkAllowed(now: number): boolean { return this.shopOpen && offersAllowed(this.catalog.balance, "hub", now, this.catalog.economy.shop.lastLossAt); }

  // ---- farm twists (MP-25)

  get twistsOpen(): boolean { return farmTwistsOpen(this.catalog.balance, this.catalog.highestLevelCleared); }
  /** The day's twist for a replay of `level` with the switch's state, or null (not cleared, too low, not open yet). */
  twist(level: number, now: number): FarmTwistView | null {
    const m = farmTwistFor(this.catalog.balance, level, utcDay(now), this.catalog.highestLevelCleared);
    return m === null ? null : { modifier: m, bonusPercent: farmBonusPercent(this.catalog.balance), on: this.stock.twist };
  }
  /** The battle card's twist switch (kept in the stock: one switch for every replay). */
  setTwist(on: boolean): void { this.stock.twist = on; }

  /**
   * A fresh battle starts on `level` with `modifiers` (null: a plain campaign run): its run modifiers. A plain replay of
   * a cleared level takes the day's twist while the switch is on; then the armed supplies are taken from the stock into
   * the run (a War Banner only into a run with a checkpoint retry: never Iron or Endless). A RETRY's modifiers come in
   * with the lost run's supplies, which are dropped first: the new attempt takes what is armed now.
   */
  prepareRun(level: number, modifiers: LevelModifiers | null, now: number): LevelModifiers | null {
    const base = modifiers === null ? null : withoutSupplies(modifiers);
    let mods = base === null || isPlainModifiers(base) ? null : base;
    if (mods === null && this.stock.twist && this.catalog.isCleared(level)) {
      const t = farmTwistFor(this.catalog.balance, level, utcDay(now), this.catalog.highestLevelCleared);
      if (t !== null) mods = farmTwistModifiers(this.catalog.balance, t);
    }
    const checkpoint = mods?.iron === undefined && mods?.endless === undefined;
    const rule = this.stock.take(this.data, { checkpoint });
    const anyTaken = rule.checkpoints > 0 || rule.scout > 0 || rule.rerolls > 0 || rule.hornTroops > 0;
    return anyTaken ? { ...(mods ?? NO_MODIFIERS), supplies: rule } : mods;
  }
}
